/**
 * CloudStorageBackend - Supabase cloud implementation for StorageService
 * 
 * Used when user is logged in. All operations go to Supabase:
 * - Working patch (autosave) → profiles.settings.workingPatch
 * - Named patches → patches table
 * - Versions → profiles.settings.versions
 */

import { MAX_VERSIONS } from '../../constants/index.js';
import { generateId } from '../../utils/index.js';
import { encodeForStorage, decodeFromStorage, clonePatchData } from '../../utils/serialization.js';

export function createCloudStorageBackend(authService) {
    
    function getClient() {
        return authService?.getSupabaseClient();
    }
    
    function getUserId() {
        return authService?.getCurrentUser()?.id;
    }
    
    function isAvailable() {
        return !!(getClient() && getUserId());
    }
    
    // ============= SETTINGS HELPERS =============
    
    /**
     * Get user's settings object from profiles table
     */
    async function _getSettings() {
        const client = getClient();
        const userId = getUserId();
        if (!client || !userId) return null;
        
        try {
            const { data, error } = await client
                .from('profiles')
                .select('settings')
                .eq('id', userId)
                .maybeSingle();
            
            if (error) {
                console.warn('[CloudBackend] _getSettings error:', error);
                return null;
            }
            
            return data?.settings || {};
        } catch (e) {
            console.warn('[CloudBackend] _getSettings exception:', e);
            return null;
        }
    }
    
    /**
     * Update user's settings object in profiles table
     */
    async function _updateSettings(settings) {
        const client = getClient();
        const userId = getUserId();
        if (!client || !userId) return false;
        
        try {
            const { error } = await client
                .from('profiles')
                .update({ settings })
                .eq('id', userId);
            
            if (error) {
                console.warn('[CloudBackend] _updateSettings error:', error);
                return false;
            }
            
            return true;
        } catch (e) {
            console.warn('[CloudBackend] _updateSettings exception:', e);
            return false;
        }
    }
    
    // ============= WORKING PATCH (AUTOSAVE) =============
    // Stored in profiles.settings.workingPatch as compressed binary
    
    async function saveWorkingPatch(patchData) {
        if (!isAvailable()) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            // [ISROUTER] Log templates being saved to cloud
            if (patchData?.templates) {
                patchData.templates.forEach(t => {
                    if (t.isRouter) {
                        console.log(`[ISROUTER] CloudBackend.saveWorkingPatch: template "${t.name}" isRouter=${t.isRouter}`);
                    }
                });
                const routerCount = patchData.templates.filter(t => t.isRouter).length;
                console.log(`[ISROUTER] CloudBackend.saveWorkingPatch: ${routerCount}/${patchData.templates.length} templates with isRouter=true`);
            }
            
            const settings = await _getSettings();
            if (settings === null) {
                return { success: false, error: 'Failed to get settings' };
            }
            
            // Encode patch data for storage
            const encoded = await encodeForStorage(patchData);
            settings.workingPatch = encoded;
            settings.workingPatchTimestamp = Date.now();
            
            if (await _updateSettings(settings)) {
                return { success: true };
            } else {
                return { success: false, error: 'Failed to update settings' };
            }
        } catch (e) {
            console.warn('[CloudBackend] saveWorkingPatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function loadWorkingPatch() {
        if (!isAvailable()) {
            return { success: false, data: null, error: 'Not logged in' };
        }
        
        try {
            const settings = await _getSettings();
            if (!settings?.workingPatch) {
                return { success: false, data: null };
            }
            
            const patchData = await decodeFromStorage(settings.workingPatch);
            
            // [ISROUTER] Log templates loaded from cloud
            if (patchData?.templates) {
                patchData.templates.forEach(t => {
                    if (t.isRouter) {
                        console.log(`[ISROUTER] CloudBackend.loadWorkingPatch: template "${t.name}" isRouter=${t.isRouter}`);
                    }
                });
                const routerCount = patchData.templates.filter(t => t.isRouter).length;
                console.log(`[ISROUTER] CloudBackend.loadWorkingPatch: ${routerCount}/${patchData.templates.length} templates with isRouter=true from cloud`);
            }
            
            return { success: true, data: patchData };
        } catch (e) {
            console.warn('[CloudBackend] loadWorkingPatch failed:', e);
            return { success: false, data: null, error: e.message };
        }
    }
    
    async function clearWorkingPatch() {
        if (!isAvailable()) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            const settings = await _getSettings();
            if (settings === null) {
                return { success: false, error: 'Failed to get settings' };
            }
            
            delete settings.workingPatch;
            delete settings.workingPatchTimestamp;
            
            if (await _updateSettings(settings)) {
                return { success: true };
            } else {
                return { success: false, error: 'Failed to update settings' };
            }
        } catch (e) {
            console.warn('[CloudBackend] clearWorkingPatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    // ============= NAMED PATCHES =============
    // Stored in patches table
    
    async function savePatch(name, patchData, deviceCount = 0, connectionCount = 0, forceInsert = false, existingPatchId = null) {
        const client = getClient();
        const userId = getUserId();
        
        if (!client || !userId) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            // Encode patch data as compressed binary
            const contentBin = await encodeForStorage(patchData);
            
            const basePayload = {
                name: name,
                content_bin: contentBin,
                device_count: deviceCount
            };
            
            // Helper to attempt save with connection_count fallback (column may not exist)
            const attemptSave = async (payload, isInsert, targetId = null) => {
                const fullPayload = { ...payload, connection_count: connectionCount };
                if (isInsert) {
                    fullPayload.user_id = userId;
                }
                
                let result;
                if (isInsert) {
                    result = await client.from('patches').insert(fullPayload).select('id').single();
                } else {
                    result = await client.from('patches').update(fullPayload).eq('id', targetId).select('id').single();
                }
                
                // Fallback without connection_count if column doesn't exist
                if (result.error && result.error.message?.includes('connection_count')) {
                    console.warn('[CloudBackend] connection_count column not found, retrying without it');
                    const fallbackPayload = isInsert ? { ...payload, user_id: userId } : payload;
                    
                    if (isInsert) {
                        return await client.from('patches').insert(fallbackPayload).select('id').single();
                    } else {
                        return await client.from('patches').update(fallbackPayload).eq('id', targetId).select('id').single();
                    }
                }
                
                return result;
            };
            
            let patchId = null;
            
            // Determine save mode:
            // - existingPatchId provided and not forceInsert: update that patch
            // - forceInsert: always create new
            // - otherwise: check if patch with same name exists
            
            if (forceInsert) {
                // Always insert new
                const { data, error } = await attemptSave(basePayload, true);
                if (error) throw error;
                patchId = data.id;
            } else if (existingPatchId) {
                // Update existing by ID
                const { data, error } = await attemptSave(basePayload, false, existingPatchId);
                if (error) {
                    // If update fails (patch deleted), fallback to insert
                    console.warn('[CloudBackend] Update failed, falling back to insert:', error.message);
                    const { data: insertData, error: insertError } = await attemptSave(basePayload, true);
                    if (insertError) throw insertError;
                    patchId = insertData.id;
                } else {
                    patchId = data.id;
                }
            } else {
                // Check if patch with this name exists
                const { data: existing } = await client
                    .from('patches')
                    .select('id')
                    .eq('user_id', userId)
                    .eq('name', name)
                    .maybeSingle();
                
                if (existing) {
                    // Update existing
                    const { data, error } = await attemptSave(basePayload, false, existing.id);
                    if (error) throw error;
                    patchId = data.id;
                } else {
                    // Insert new
                    const { data, error } = await attemptSave(basePayload, true);
                    if (error) throw error;
                    patchId = data.id;
                }
            }
            
            return { success: true, id: patchId, name: name };
        } catch (e) {
            console.warn('[CloudBackend] savePatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function loadPatch(identifier) {
        const client = getClient();
        const userId = getUserId();
        
        if (!client || !userId) {
            return { success: false, data: null, error: 'Not logged in' };
        }
        
        try {
            const { data, error } = await client
                .from('patches')
                .select('id, name, content_bin')
                .eq('id', identifier)
                .single();
            
            if (error) throw error;
            if (!data) {
                return { success: false, data: null, error: 'Patch not found' };
            }
            
            if (!data.content_bin) {
                return { success: false, data: null, error: 'Patch has no content' };
            }
            
            const patchData = await decodeFromStorage(data.content_bin);
            
            return {
                success: true,
                data: patchData,
                name: data.name,
                id: data.id
            };
        } catch (e) {
            console.warn('[CloudBackend] loadPatch failed:', e);
            return { success: false, data: null, error: e.message };
        }
    }
    
    async function deletePatch(identifier) {
        const client = getClient();
        const userId = getUserId();
        
        if (!client || !userId) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            const { error } = await client
                .from('patches')
                .delete()
                .eq('id', identifier);
            
            if (error) throw error;
            
            return { success: true };
        } catch (e) {
            console.warn('[CloudBackend] deletePatch failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function listPatches() {
        const client = getClient();
        const userId = getUserId();
        
        if (!client || !userId) {
            return [];
        }
        
        try {
            // Try with connection_count first
            let { data, error } = await client
                .from('patches')
                .select('id, name, device_count, connection_count, created_at, updated_at')
                .eq('user_id', userId)
                .order('updated_at', { ascending: false });
            
            // Fallback without connection_count
            if (error && error.message?.includes('connection_count')) {
                console.warn('[CloudBackend] connection_count column not found, retrying without it');
                const fallback = await client
                    .from('patches')
                    .select('id, name, device_count, created_at, updated_at')
                    .eq('user_id', userId)
                    .order('updated_at', { ascending: false });
                data = fallback.data;
                error = fallback.error;
            }
            
            if (error) throw error;
            
            return (data || []).map(p => ({
                id: p.id,
                name: p.name,
                deviceCount: p.device_count || 0,
                connectionCount: p.connection_count ?? 0,
                timestamp: new Date(p.updated_at).getTime()
            }));
        } catch (e) {
            console.warn('[CloudBackend] listPatches failed:', e);
            return [];
        }
    }
    
    // ============= VERSIONS =============
    // Stored in profiles.settings.versions as array of compressed version objects
    
    async function saveVersion(name, patchData, deviceCount = 0, connectionCount = 0) {
        if (!isAvailable()) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            const settings = await _getSettings();
            if (settings === null) {
                return { success: false, error: 'Failed to get settings' };
            }
            
            const versions = settings.versions || [];
            
            // Encode patch data for storage
            const encoded = await encodeForStorage(patchData);
            
            const version = {
                id: generateId(),
                name: name || `Version ${versions.length + 1}`,
                timestamp: Date.now(),
                deviceCount: deviceCount,
                connectionCount: connectionCount,
                data: encoded
            };
            
            // Add to front (newest first)
            versions.unshift(version);
            
            // Keep only last MAX_VERSIONS
            while (versions.length > MAX_VERSIONS) {
                versions.pop();
            }
            
            settings.versions = versions;
            
            if (await _updateSettings(settings)) {
                // Return version without encoded data
                return {
                    success: true,
                    version: {
                        id: version.id,
                        name: version.name,
                        timestamp: version.timestamp,
                        deviceCount: version.deviceCount,
                        connectionCount: version.connectionCount
                    }
                };
            } else {
                return { success: false, error: 'Failed to update settings' };
            }
        } catch (e) {
            console.warn('[CloudBackend] saveVersion failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function loadVersion(versionId) {
        if (!isAvailable()) {
            return { success: false, data: null, error: 'Not logged in' };
        }
        
        try {
            const settings = await _getSettings();
            if (!settings?.versions) {
                return { success: false, data: null, error: 'No versions found' };
            }
            
            const version = settings.versions.find(v => v.id === versionId);
            if (!version) {
                return { success: false, data: null, error: 'Version not found' };
            }
            
            const patchData = await decodeFromStorage(version.data);
            
            return {
                success: true,
                data: patchData,
                name: version.name,
                id: version.id
            };
        } catch (e) {
            console.warn('[CloudBackend] loadVersion failed:', e);
            return { success: false, data: null, error: e.message };
        }
    }
    
    async function deleteVersion(versionId) {
        if (!isAvailable()) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            const settings = await _getSettings();
            if (!settings?.versions) {
                return { success: false, error: 'No versions found' };
            }
            
            const version = settings.versions.find(v => v.id === versionId);
            if (!version) {
                return { success: false, error: 'Version not found' };
            }
            
            settings.versions = settings.versions.filter(v => v.id !== versionId);
            
            if (await _updateSettings(settings)) {
                return { success: true, deletedName: version.name };
            } else {
                return { success: false, error: 'Failed to update settings' };
            }
        } catch (e) {
            console.warn('[CloudBackend] deleteVersion failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    async function listVersions() {
        if (!isAvailable()) {
            return [];
        }
        
        try {
            const settings = await _getSettings();
            if (!settings?.versions) {
                return [];
            }
            
            return settings.versions.map(v => ({
                id: v.id,
                name: v.name,
                timestamp: v.timestamp,
                deviceCount: v.deviceCount || 0,
                connectionCount: v.connectionCount || 0
                // data not included in list
            }));
        } catch (e) {
            console.warn('[CloudBackend] listVersions failed:', e);
            return [];
        }
    }
    
    async function renameVersion(versionId, newName) {
        if (!isAvailable()) {
            return { success: false, error: 'Not logged in' };
        }
        
        try {
            const settings = await _getSettings();
            if (!settings?.versions) {
                return { success: false, error: 'No versions found' };
            }
            
            const version = settings.versions.find(v => v.id === versionId);
            if (!version) {
                return { success: false, error: 'Version not found' };
            }
            
            version.name = newName;
            
            if (await _updateSettings(settings)) {
                return { success: true };
            } else {
                return { success: false, error: 'Failed to update settings' };
            }
        } catch (e) {
            console.warn('[CloudBackend] renameVersion failed:', e);
            return { success: false, error: e.message };
        }
    }
    
    // ============= PUBLIC API =============
    
    return {
        // Status
        isAvailable,
        
        // Working patch
        saveWorkingPatch,
        loadWorkingPatch,
        clearWorkingPatch,
        
        // Named patches
        savePatch,
        loadPatch,
        deletePatch,
        listPatches,
        
        // Versions
        saveVersion,
        loadVersion,
        deleteVersion,
        listVersions,
        renameVersion
    };
}
